/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  DEFAULT_CZECH_TEXT_TO_SPEECH_MODEL,
  DEFAULT_CHINESE_TEXT_TO_SPEECH_MODEL,
  DEFAULT_ARABIC_TEXT_TO_SPEECH_MODEL,
  DEFAULT_DUTCH_BELGIAN_TEXT_TO_SPEECH_MODEL,
  DEFAULT_ENGLISH_US_TEXT_TO_SPEECH_MODEL,
  DEFAULT_FRENCH_FRANCE_TEXT_TO_SPEECH_MODEL,
  DEFAULT_GERMAN_TEXT_TO_SPEECH_MODEL,
  DEFAULT_ITALIAN_TEXT_TO_SPEECH_MODEL,
  DEFAULT_JAPANESE_TEXT_TO_SPEECH_MODEL,
  DEFAULT_KOREAN_TEXT_TO_SPEECH_MODEL,
  DEFAULT_PORTUGUESE_TEXT_TO_SPEECH_MODEL,
  DEFAULT_SPANISH_NA_TEXT_TO_SPEECH_MODEL,
  DEFAULT_SWEDISH_TEXT_TO_SPEECH_MODEL,
  DEFAULT_TEXT_TO_SPEECH_VISUALIZER_TEXT,
  DEFAULT_ENGLISH_US_TEXT_TO_SPEECH_MODEL_CPD
} from '../components/phone/constants';

// In CPD, only certain default languages are available
export const getDefaultTTSModelByLanguage = (assistantLanguage, isCpd) => {
  if (isCpd) {
    return DEFAULT_ENGLISH_US_TEXT_TO_SPEECH_MODEL_CPD;
  }

  const language = assistantLanguage?.split('-')[0];
  switch (language) {
    case 'cs':
      return DEFAULT_CZECH_TEXT_TO_SPEECH_MODEL;
    case 'zh':
      return DEFAULT_CHINESE_TEXT_TO_SPEECH_MODEL;
    case 'ar':
      return DEFAULT_ARABIC_TEXT_TO_SPEECH_MODEL;
    case 'nl':
      return DEFAULT_DUTCH_BELGIAN_TEXT_TO_SPEECH_MODEL;
    case 'en':
      return DEFAULT_ENGLISH_US_TEXT_TO_SPEECH_MODEL;
    case 'fr':
      return DEFAULT_FRENCH_FRANCE_TEXT_TO_SPEECH_MODEL;
    case 'de':
      return DEFAULT_GERMAN_TEXT_TO_SPEECH_MODEL;
    case 'it':
      return DEFAULT_ITALIAN_TEXT_TO_SPEECH_MODEL;
    case 'ja':
      return DEFAULT_JAPANESE_TEXT_TO_SPEECH_MODEL;
    case 'ko':
      return DEFAULT_KOREAN_TEXT_TO_SPEECH_MODEL;
    case 'pt':
      return DEFAULT_PORTUGUESE_TEXT_TO_SPEECH_MODEL;
    case 'es':
      return DEFAULT_SPANISH_NA_TEXT_TO_SPEECH_MODEL;
    case 'sv':
      return DEFAULT_SWEDISH_TEXT_TO_SPEECH_MODEL;
    default:
      return DEFAULT_ENGLISH_US_TEXT_TO_SPEECH_MODEL;
  }
};

export const getTTSModelLanguageText = language => {
  switch (language.substring(0, 2)) {
    case 'cs':
      return 'Czech';
    case 'zh':
      return 'Chinese';
    case 'ar':
      return 'Arabic';
    case 'nl':
      return 'Dutch';
    case 'en':
      return 'English';
    case 'fr':
      return 'French';
    case 'de':
      return 'German';
    case 'it':
      return 'Italian';
    case 'ja':
      return 'Japanese';
    case 'ko':
      return 'Korean';
    case 'pt':
      return 'Portuguese';
    case 'es':
      return 'Spanish';
    case 'sv':
      return 'Swedish';
    default:
      return language;
  }
};

export const getTTSModelCountry = language => {
  switch (language.substring(0, 2)) {
    case 'US':
      return 'United States';
    case 'GB':
      return 'United Kingdom';
    case 'CA':
      return 'Canadian';
    case 'FR':
      return 'France';
    case 'ES':
      return 'Castilian';
    case 'LA':
      return 'Latin American';
    case 'BE':
      return 'Belgian';
    case 'NL':
      return 'Netherlands';
    case 'AU':
      return 'Australian';
    default:
      return '';
  }
};

export const getTTSModelLanguageDescription = assistantLanguage => {
  if (assistantLanguage) {
    const languageSplit = assistantLanguage?.split('-');
    if (languageSplit.length > 1) {
      return (
        getTTSModelLanguageText(languageSplit[0]) + ' - ' + languageSplit[1]
      );
    } else {
      return getTTSModelLanguageText(assistantLanguage);
    }
  }
  return assistantLanguage;
};

export const getTTSModelLanguageWithCountryDescription = assistantLanguage => {
  if (assistantLanguage) {
    const languageSplit = assistantLanguage?.split('-');
    const language = getTTSModelLanguageText(languageSplit[0]);
    if (assistantLanguage.startsWith('es-US')) {
      return language + ' - North American';
    }
    if (languageSplit.length > 1) {
      const country = getTTSModelCountry(languageSplit[1]);
      if (country) {
        return (
          getTTSModelLanguageText(languageSplit[0]) +
          ' - ' +
          getTTSModelCountry(languageSplit[1])
        );
      }
      return language;
    }
  }
  return assistantLanguage;
};

export const isModelExpressive = language => {
  return language.toLowerCase().includes('expressive');
};

export const getVoiceName = friendlyName => {
  const startIndex = friendlyName?.indexOf(': ') + 2;
  const endIndex = friendlyName?.indexOf(',');

  return friendlyName?.substring(startIndex, endIndex);
};

export const getDefaultLanguageText = language => {
  if (language && DEFAULT_TEXT_TO_SPEECH_VISUALIZER_TEXT[language]) {
    return DEFAULT_TEXT_TO_SPEECH_VISUALIZER_TEXT[language];
  }
  return DEFAULT_TEXT_TO_SPEECH_VISUALIZER_TEXT['en'];
};
